<template>
  <q-card
    v-touch-hold.mouse="onAddMultipleTrials"
    v-ripple="targetIsOpen"
    class="col-auto column full-height noselect"
    :class="{
      'bg-primary': isSelected,
      'text-white': isSelected,
      'bg-green-1': hasReachedMinTrials,
      'cursor-pointer': targetIsOpen,
    }"
    @click="onSelectTarget"
  >
    <q-card-section
      v-if="hasDiscriminativeStimulus"
      class="bg-primary-variant text-white"
      :class="{ 'q-py-none': tiny, 'q-py-xs': !tiny }"
    >
      <q-badge
        v-if="hasTotalDuration"
        floating
        :style="promptLevelStyle"
      >
        {{ totalDurationFormatted }}
      </q-badge>
      {{ discriminativeStimulus }}
    </q-card-section>
    <q-card-section
      class="col row items-center"
      :class="{
        'text-overline text-uppercase text-dark-variant duration-overline q-pt-xs q-pb-none': tiny,
        'q-py-sm': dense && !tiny,
      }"
    >
      <q-badge
        v-if="!hasDiscriminativeStimulus && hasTotalDuration"
        floating
        :style="promptLevelStyle"
      >
        {{ totalDurationFormatted }}
      </q-badge>
      <div
        class="row items-center"
      >
        <q-chip
          v-if="!tiny"
          size="sm"
          :text-color="getTextColor(getTargetWorkflowProperty('color', ''))"
          :color="getTargetWorkflowProperty('color', '')"
          :style="{ backgroundColor: getTargetWorkflowProperty('color', '') }"
          :icon="getTargetWorkflowProperty('icon', 'help')"
          class="chip-no-label"
        />
        <span class="q-ml-xs">{{ target.label }}</span>
      </div>
    </q-card-section>
    <q-card-section
      class="text-center full-width text-subtitle2 bg-grey-1 q-py-xs"
    >
      {{ durationFormatted }}
    </q-card-section>
    <q-menu
      v-if="showPromptLevelOptions"
      :anchor="promptLevelsMenuPosition"
      transition-show="none"
      transition-hide="none"
      auto-close
      :class="showAddBehavior ? 'prompt-levels-menu--detached' : ''"
      @hide="isSelected = false"
    >
      <q-list
        style="min-width: 100px"
        :class="showAddBehavior ? 'bg-white rounded-borders shadow-2' : ''"
      >
        <q-item
          v-for="promptLevel in programPrompts"
          :key="promptLevel.id"
          clickable
          :style="getPromptLevelStyle(promptLevel)"
          class="prompt-level"
          @click="onSelectPromptLevel(promptLevel)"
        >
          <q-item-section>{{ promptLevel.trialOutcome }}</q-item-section>
        </q-item>
      </q-list>
      <add-behavior-occurrence-button
        v-if="showAddBehavior"
        @add="onAddBehaviorOccurrence"
      />
    </q-menu>
  </q-card>
</template>
<script>
import AddMultipleTrials from 'components/Modals/AddMultipleTrials';
import AddBehaviorOccurrenceButton from 'components/Programs/DataCollection/AddBehaviorOccurrenceButton';
import { Timestamp } from 'firebase/firestore';
import { chain, get, head, isDate, isEmpty } from 'lodash';
import { useBehaviorOccurrenceRecorder } from 'src/composables/useBehaviorOccurrenceRecorder';
import DataCollection from 'src/mixins/DataCollection';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { getTextColor } from 'src/utils/TargetUtils';

export default {
  name: 'DurationData',

  components: { AddBehaviorOccurrenceButton },

  mixins: [Targets, DataCollection, Toolbox, Interactions],

  emits: ['record', 'record-program-data'],

  setup(props) {
    return useBehaviorOccurrenceRecorder(props);
  },

  data() {
    return {
      timerStart: null,

      duration: 0,

      isSelected: false,

      timerInterval: null,
    };
  },

  computed: {

    isTimerStarted() {
      return (this.timerInterval !== null);
    }, // isTimerStarted()

    durationOffset() {
      const timerOffsetTimestamp = get(this.data, `timerOffset_${this.target.id}`);
      if (chain(timerOffsetTimestamp).get('toDate').isFunction().value()) {
        return timerOffsetTimestamp.toDate();
      }
      return null;
    },

    elapsedDuration() {
      return get(this.data, `timerDuration_${this.target.id}`) || 0;
    },

    selectedPromptLevel() {
      const id = get(this.data, `timerPrompt_${this.target.id}`);
      return chain(this.programPrompts).find({ id }).defaultTo(head(this.programPrompts)).value();
    },

    promptLevelStyle() {
      if (this.isTimerStarted && this.selectedPromptLevel) {
        return this.getPromptLevelStyle(this.selectedPromptLevel);
      }
      return this.previousPromptLevelStyle;
    }, // promptLevelStyle()

    durationFormatted() {
      if (this.currentDuration === 0) {
        return '-';
      }
      return this.formatDuration(this.currentDuration, false);
    }, // durationFormatted()

    hasTotalDuration() {
      return this.totalDuration > 0;
    }, // hasTotalDuration()

    hasData() {
      return !chain(this.data)
        .get('data')
        .filter({ target: this.target.id })
        .isEmpty()
        .value();
    }, // hasData()

    durationPromptLevelTiming() {
      return this.target?.durationPromptLevelTiming
        ?? this.sessionProgram?.program?.durationPromptLevelTiming
        ?? 'start';
    }, // durationPromptLevelTiming()

    fillZeroDuration() {
      return this.target?.fillZeroDuration ?? this.sessionProgram?.program?.fillZeroDuration ?? false;
    }, // fillZeroDuration()

    showPromptLevelOptions() {
      return this.hasMultiplePromptLevels && this.targetIsOpen
        && (
          (this.durationPromptLevelTiming === 'start' && !this.isTimerStarted)
          || (this.durationPromptLevelTiming === 'end' && this.isTimerStarted)
        );
    }, // showPromptLevelOptions()

    promptLevelsMenuPosition() {
      if (this.$q.platform.is.ipad) {
        return 'bottom left';
      }

      return this.$q.screen.gt.sm ? 'top right' : undefined;
    }, // promptLevelsMenuPosition()

    currentDuration() {
      return this.elapsedDuration + this.duration;
    },

    pastDuration() {
      return chain(this.data)
        .get('data')
        .filter({ target: this.target.id })
        .sumBy('duration')
        .value();
    }, // pastDuration()

    totalDuration() {
      if (!get(this.target, 'id')
        || !get(this.data, 'data')) {
        return (this.currentDuration);
      }

      return this.pastDuration + this.elapsedDuration + this.duration;
    }, // totalDuration()

    totalDurationFormatted() {
      return this.formatDuration(this.totalDuration, false);
    }, // totalDurationFormatted()

  },

  watch: {
    durationOffset: {
      immediate: true,
      handler(newDurationOffset, prevDurationOffset) {
        if (isDate(newDurationOffset) && isDate(prevDurationOffset)
        && (newDurationOffset.getTime() === prevDurationOffset.getTime())) {
          return;
        }
        if (newDurationOffset === null) {
          this.stopTimer();
        } else if (!this.isTimerStarted && !this.sessionPaused) {
          this.startTimer(newDurationOffset);
        }
      },
    },

    sessionPaused: {
      immediate: false,
      handler(sessionPausedState) {
        if (sessionPausedState) {
          if (!this.isTimerStarted) {
            return;
          }

          this.recordDuration(this.currentDuration);
          this.stopTimer();
        } else if (this.durationOffset !== null) {
          const timerOffset = this.sessionDurationOffset || new Date();
          this.recordDuration(this.currentDuration, timerOffset);
          this.startTimer(timerOffset);
        }
      },
    },
  },

  beforeUnmount() {
    clearInterval(this.timerInterval);
  }, // beforeUnmount()

  methods: {
    getTextColor,

    /**
     * Called by parent to signal session finish
     */
    finishSession() {
      if (this.isTimerStarted) {
        const data = this.recordData(this.selectedPromptLevel, this.currentDuration);
        this.stopTimer();
        if (!isEmpty(data)) {
          return [{
            target: this.target.id,
            ...data,
          }];
        }
      } else if (!this.hasData && this.fillZeroDuration) {
        return [{
          phantomTrial: true,
          target: this.target.id,
          duration: 0,
        }];
      }
    }, // finishSession()

    onAddMultipleTrials() {
      if (this.isTimerStarted || !this.targetIsOpen) {
        return;
      }

      this.openComponentDialog(AddMultipleTrials, {
        program: this.sessionProgram,
        // component needs only promptLevels array in the object
        promptLevelTemplate: { promptLevels: this.programPrompts },
        showDuration: true,
        duration: 0,
        showAddBehavior: this.showAddBehavior,
        targetLabel: this.target.label ?? '',
      }, ({ data, duration, behaviorOccurrences }) => {
        // manually entered duration of all selected promptLevels including the existing target duration
        // record data of each active promptLevel
        data.forEach((activePromptLevel) => {
          const { promptLevel, value } = activePromptLevel;

          if (value > 0) {
            this.onEditSessionProgramTargetValue(promptLevel, value, duration);
          }
        });
        this.recordOccurrences(behaviorOccurrences);
      });
    }, // onAddMultipleTrials()

    onSelectTarget() {
      if (!this.selectedPromptLevel) {
        this.notifyWarning(this.$t('run.collectData.missingTargetPromptLevelSelected'));
      }

      if (!this.targetIsOpen || !this.selectedPromptLevel) {
        return;
      }

      this.notifyDeletedPromptLevelTemplate();

      const shouldStopTimerWithPromptTimingStart = this.isTimerStarted && this.durationPromptLevelTiming === 'start';
      const shouldStopTimerWithPromptTimingEnd = this.isTimerStarted
        && this.durationPromptLevelTiming === 'end' && !this.hasMultiplePromptLevels;

      if (shouldStopTimerWithPromptTimingStart || shouldStopTimerWithPromptTimingEnd) {
        this.isSelected = false;
        const data = this.recordData(this.selectedPromptLevel, this.currentDuration);
        this.$emit('record', data);
        this.recordTimerEnd();

        return;
      }

      if (!this.hasMultiplePromptLevels
        || (this.durationPromptLevelTiming === 'end')) {
        this.recordTimerStart();
        return;
      }

      this.isSelected = true;
    }, // onSelectTarget()

    onSelectPromptLevel(promptLevel) {
      if (!this.targetIsOpen) {
        return;
      }

      if (this.durationPromptLevelTiming === 'start') {
        this.recordTimerStart(promptLevel);
      } else {
        const data = this.recordData(promptLevel, this.currentDuration);
        this.$emit('record', data);
        this.recordTimerEnd();
      }
    }, // onSelectPromptLevel()

    startTimer(timeOffset = null) {
      this.isSelected = false;

      if (this.isTimerStarted) {
        // Prevent multiple simultanous timers
        return;
      }

      this.timerStart = timeOffset || new Date();

      const initTime = new Date();
      this.duration = (initTime.getTime() - this.timerStart.getTime()) / 1000;

      this.timerInterval = setInterval(() => {
        const curTime = new Date();
        this.duration = (curTime.getTime() - this.timerStart.getTime()) / 1000;
      }, 50);
    }, // startTimer()

    stopTimer() {
      if (!this.isTimerStarted) {
        return;
      }

      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.timerStart = null;

      this.duration = 0;
    }, // stopTimer()

    recordTimerStart(promptLevel = null) {
      const data = {
        [`timerOffset_${this.target.id}`]: Timestamp.now(),
      };
      if (promptLevel !== null) {
        data[`timerPrompt_${this.target.id}`] = promptLevel.id;
      }
      this.$emit(
        'record-program-data',
        this.sessionProgram,
        data,
      );
    },

    recordTimerEnd() {
      const data = {
        [`timerOffset_${this.target.id}`]: null,
        [`timerDuration_${this.target.id}`]: 0,
      };
      this.$emit(
        'record-program-data',
        this.sessionProgram,
        data,
      );
    },

    recordData(promptLevel, duration) {
      const data = {
        promptLevel: get(promptLevel, 'id', null),
        duration,
        scoreValue: get(promptLevel, 'score', null),
      };
      const targetPromptLevelTemplate = this.getActivePromptLevelTemplateId(get(this.target, 'promptLevelTemplate'));

      if (targetPromptLevelTemplate) {
        Object.assign(data, { promptLevelTemplate: targetPromptLevelTemplate });
      }

      return data;
    }, // recordData()

    recordDuration(duration, timerOffsetTimestampDate = null) {
      const timerOffsetTimestamp = (timerOffsetTimestampDate !== null)
        ? Timestamp.fromDate(timerOffsetTimestampDate)
        : null;
      const dataObj = {
        [`timerDuration_${this.target.id}`]: duration,
      };
      if (timerOffsetTimestamp !== null) {
        dataObj[`timerOffset_${this.target.id}`] = timerOffsetTimestamp;
      }
      this.$emit('record-program-data', this.sessionProgram, dataObj);
    },

    onEditSessionProgramTargetValue(promptLevel, quantity, duration) {
      if (this.hasMultiplePromptLevels && !promptLevel) {
        return;
      }

      const selected = promptLevel || head(this.programPrompts);
      for (let i = 0; i < quantity; i += 1) {
        const data = this.recordData(selected, duration);
        this.$emit('record', data);
      }
      this.isSelected = false;
    }, // onEditSessionProgramTargetValue()

  },

};
</script>
<style scoped>

.prompt-level {
  border-width: 0px 5px;
  border-style: solid;
}

.duration-overline {
  font-size: 9px;
  line-height:1rem;
}

</style>
